import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth, FREE_LIMIT } from '../context/AuthContext';
import { analytics } from '../lib/analytics';
import { supabase, generateApiKey, fetchApiKey, revokeApiKey, fetchMyTeams, fetchTeamMembers, removeMember, updateMemberRole } from '../lib/supabase';

// ── Nav sections ─────────────────────────────────────────────
const NAV = [
  { id: 'profile',   label: 'Profile',        icon: '◎' },
  { id: 'security',  label: 'Security',        icon: '🔒' },
  { id: 'plan',      label: 'Plan & billing',  icon: '⚡' },
  { id: 'teams',     label: 'Teams',           icon: '🏢', proOnly: true },
  { id: 'developer', label: 'Developer',       icon: '⌨' },
  { id: 'library',   label: 'Library',         icon: '📚' },
  { id: 'session',   label: 'Session',         icon: '→' },
];

// ── Duplicate Finder Card ───────────────────────────────────────────────────
function DuplicateFinderCard({ userId }) {
  const [scanning,   setScanning]   = useState(false);
  const [dupeGroups, setDupeGroups] = useState(null); // null = not yet scanned
  const [deleting,   setDeleting]   = useState(new Set());
  const [deleted,    setDeleted]    = useState(new Set());

  const scan = async () => {
    if (!userId) return;
    setScanning(true);
    try {
      const { supabase } = await import('../lib/supabase');
      const { data } = await supabase
        .from('entries')
        .select('id, title, created_at, type')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (!data) { setDupeGroups([]); return; }

      // Group by normalised title
      const groups = {};
      data.forEach(e => {
        const key = e.title.trim().toLowerCase();
        if (!groups[key]) groups[key] = [];
        groups[key].push(e);
      });

      // Keep only groups with >1 entry
      const dupes = Object.values(groups).filter(g => g.length > 1);
      setDupeGroups(dupes);
    } catch (err) { alert('Scan failed: ' + err.message); }
    setScanning(false);
  };

  const deleteEntry = async (id) => {
    setDeleting(prev => new Set([...prev, id]));
    try {
      const { supabase } = await import('../lib/supabase');
      await supabase.from('entries').delete().eq('id', id);
      setDeleted(prev => new Set([...prev, id]));
    } catch (err) { alert('Delete failed: ' + err.message); }
    setDeleting(prev => { const n = new Set(prev); n.delete(id); return n; });
  };

  const totalDupes = dupeGroups?.reduce((s, g) => s + g.length - 1, 0) ?? 0;

  return (
    <div style={{ background: 'var(--surface)', border: '0.5px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
      <div style={{ padding: '14px 18px', borderBottom: dupeGroups !== null ? '0.5px solid var(--border)' : 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text)', marginBottom: 3 }}>Find duplicate prompts</div>
            <div style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.5 }}>
              Scans your library for entries with the same title and lets you remove the extras.
            </div>
          </div>
          <button onClick={scan} disabled={scanning} style={{
            background: 'var(--accent)', color: '#fff', border: 'none',
            borderRadius: 8, padding: '8px 16px', fontFamily: 'var(--font)',
            fontSize: 13, fontWeight: 500, cursor: scanning ? 'wait' : 'pointer',
            opacity: scanning ? 0.7 : 1, whiteSpace: 'nowrap',
          }}>
            {scanning ? 'Scanning…' : dupeGroups === null ? 'Scan library' : '↺ Re-scan'}
          </button>
        </div>
      </div>

      {dupeGroups !== null && (
        <div style={{ padding: '14px 18px' }}>
          {dupeGroups.length === 0 ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#1d9e75', fontSize: 13 }}>
              <span>✓</span> No duplicates found — your library is clean!
            </div>
          ) : (
            <>
              <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 12 }}>
                Found <strong style={{ color: 'var(--text)' }}>{totalDupes} duplicate{totalDupes !== 1 ? 's' : ''}</strong> across {dupeGroups.length} title{dupeGroups.length !== 1 ? 's' : ''}.
                The newest copy is kept — delete the older ones below.
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {dupeGroups.map((group, gi) => (
                  <div key={gi} style={{ border: '0.5px solid var(--border2)', borderRadius: 8, overflow: 'hidden' }}>
                    <div style={{ padding: '8px 12px', background: 'var(--bg)', fontSize: 12, fontWeight: 600, color: 'var(--text)', borderBottom: '0.5px solid var(--border)' }}>
                      "{group[0].title}" — {group.length} copies
                    </div>
                    {group.map((entry, ei) => {
                      const isNewest  = ei === 0;
                      const isDeleted = deleted.has(entry.id);
                      const isDeleting = deleting.has(entry.id);
                      return (
                        <div key={entry.id} style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          padding: '8px 12px', gap: 8,
                          background: isDeleted ? 'rgba(29,158,117,0.05)' : isNewest ? 'transparent' : 'var(--surface)',
                          opacity: isDeleted ? 0.5 : 1,
                          borderBottom: ei < group.length - 1 ? '0.5px solid var(--border)' : 'none',
                        }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <span style={{ fontSize: 11, color: 'var(--dim)' }}>
                              {new Date(entry.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </span>
                            {isNewest && (
                              <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 6, background: 'rgba(29,158,117,0.1)', color: '#1d9e75', border: '0.5px solid rgba(29,158,117,0.3)' }}>
                                keep
                              </span>
                            )}
                          </div>
                          {!isNewest && !isDeleted && (
                            <button onClick={() => deleteEntry(entry.id)} disabled={isDeleting} style={{
                              background: 'none', border: '0.5px solid rgba(226,75,74,0.3)',
                              borderRadius: 6, padding: '3px 10px', color: 'var(--danger)',
                              fontFamily: 'var(--font)', fontSize: 11, cursor: 'pointer',
                              opacity: isDeleting ? 0.5 : 1,
                            }}>
                              {isDeleting ? '…' : 'Delete'}
                            </button>
                          )}
                          {isDeleted && <span style={{ fontSize: 11, color: '#1d9e75' }}>✓ Deleted</span>}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function SettingsPage() {
  const { user, profile, isPro, isAdmin, updateEmail, updatePassword, updateProfile, refreshProfile, signOut, entryCount } = useAuth();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('profile');

  const [displayName, setDisplayName] = useState(profile?.display_name || '');
  const [newUsername, setNewUsername]   = useState('');
  const [usernameAvail, setUsernameAvail] = useState(null);
  const [usernameChecking, setUsernameChecking] = useState(false);
  const [showUsernameForm, setShowUsernameForm] = useState(false);
  const [email, setEmail]             = useState(user?.email || '');
  const [newPass, setNewPass]         = useState('');
  const [confirmPass, setConfirmPass] = useState('');
  const [saving, setSaving]   = useState({});
  const [success, setSuccess] = useState({});
  const [errors, setErrors]   = useState({});

  const [apiKey, setApiKey]               = useState(null);
  const [newKeyPlaintext, setNewKeyPlaintext] = useState('');
  const [apiKeyLoading, setApiKeyLoading] = useState(true);
  const [apiKeyWorking, setApiKeyWorking] = useState(false);
  const [showRevokeConfirm, setShowRevokeConfirm] = useState(false);
  const [keyCopied, setKeyCopied]         = useState(false);

  const [myTeams, setMyTeams]           = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [expandedTeam, setExpandedTeam] = useState(null);
  const [teamMembers, setTeamMembers]   = useState({});
  const [memberLoading, setMemberLoading] = useState({});
  const [roleWorking, setRoleWorking]   = useState({});

  const [invoices, setInvoices]               = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [cancelLoading, setCancelLoading]     = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [canceledUntil, setCanceledUntil]     = useState(null);

  const hasStripe  = !!(profile?.stripe_customer_id);
  const [aiUsage, setAiUsage] = useState(null);
  const showBilling = (isPro || isAdmin) && hasStripe;

  useEffect(() => {
    if (!user) return;
    fetchApiKey(user.id).then(setApiKey).catch(console.error).finally(() => setApiKeyLoading(false));
  }, [user?.id]);

  useEffect(() => {
    if (!user || (!isPro && !isAdmin)) return;
    // Fetch AI usage quota via the API key endpoint
    fetch('/api/ai-usage', {
      headers: { 'Content-Type': 'application/json' },
    }).catch(() => {});
    // Use supabase directly for usage since we don't want to expose API key in frontend
    import('../lib/supabase').then(({ supabase }) => {
      const start = new Date();
      start.setDate(1); start.setHours(0,0,0,0);
      supabase.from('ai_usage')
        .select('feature', { count: 'exact' })
        .eq('user_id', user.id)
        .gte('created_at', start.toISOString())
        .then(({ data, count }) => {
          const byFeature = (data || []).reduce((acc, r) => {
            acc[r.feature] = (acc[r.feature] || 0) + 1; return acc;
          }, {});
          const limit = isAdmin ? 999 : 20;
          setAiUsage({ total: count || 0, limit, byFeature,
            resets: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) });
        }).catch(() => {});
    });
  }, [user?.id, isPro, isAdmin]);

  useEffect(() => {
    if (!user || (!isPro && !isAdmin)) return;
    setTeamsLoading(true);
    fetchMyTeams(user.id).then(async (teams) => {
      setMyTeams(teams);
      // Pre-load member counts for all teams so seat usage shows immediately
      for (const team of teams) {
        try {
          const members = await fetchTeamMembers(team.id);
          setTeamMembers(m => ({ ...m, [team.id]: members }));
        } catch (e) { console.error(e); }
      }
    }).catch(console.error).finally(() => setTeamsLoading(false));
  }, [user?.id, isPro, isAdmin]);

  useEffect(() => {
    if (!showBilling) return;
    setInvoicesLoading(true);
    fetch(`/api/billing?customerId=${profile.stripe_customer_id}`)
      .then(r => r.json()).then(d => { if (d.invoices) setInvoices(d.invoices); })
      .catch(console.error).finally(() => setInvoicesLoading(false));
  }, [showBilling, profile?.stripe_customer_id]);

  const setStatus = (key, type, msg) => {
    if (type === 'success') setSuccess(s => ({ ...s, [key]: msg }));
    if (type === 'error')   setErrors(s => ({ ...s, [key]: msg }));
    setTimeout(() => { setSuccess(s => ({ ...s, [key]: '' })); setErrors(s => ({ ...s, [key]: '' })); }, 3500);
  };

  const fmt       = ts => new Date(ts * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const fmtAmount = (amt, currency) => (amt / 100).toLocaleString('en-US', { style: 'currency', currency: currency.toUpperCase() });

  const USERNAME_COOLDOWN_DAYS = 7;
  const lastChanged      = profile?.username_changed_at ? new Date(profile.username_changed_at) : null;
  const daysSinceChange  = lastChanged ? (Date.now() - lastChanged.getTime()) / (1000 * 60 * 60 * 24) : 999;
  const canChangeUsername = daysSinceChange >= USERNAME_COOLDOWN_DAYS;
  const nextChangeDate   = lastChanged
    ? new Date(lastChanged.getTime() + USERNAME_COOLDOWN_DAYS * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : null;

  useEffect(() => {
    if (!newUsername || newUsername.length < 3) { setUsernameAvail(null); return; }
    if (!/^[a-z0-9_]+$/.test(newUsername)) { setUsernameAvail(false); return; }
    if (newUsername === profile?.username) { setUsernameAvail(null); return; }
    setUsernameChecking(true);
    const timer = setTimeout(async () => {
      const { data } = await supabase.from('profiles').select('id').eq('username', newUsername).single();
      setUsernameAvail(!data);
      setUsernameChecking(false);
    }, 400);
    return () => clearTimeout(timer);
  }, [newUsername]);

  const saveUsername = async () => {
    if (!usernameAvail || !newUsername.trim()) return;
    setSaving(s => ({ ...s, username: true }));
    try {
      const { error } = await supabase.from('profiles').update({ username: newUsername.trim(), username_changed_at: new Date().toISOString() }).eq('id', user.id);
      if (error) throw error;
      await refreshProfile();
      setShowUsernameForm(false); setNewUsername(''); setUsernameAvail(null);
      setStatus('username', 'success', 'Username updated');
    } catch (e) { setStatus('username', 'error', e.message); }
    setSaving(s => ({ ...s, username: false }));
  };

  const saveProfile = async () => {
    if (!displayName.trim()) return;
    setSaving(s => ({ ...s, profile: true }));
    try { await updateProfile({ display_name: displayName.trim() }); setStatus('profile', 'success', 'Display name updated'); }
    catch (e) { setStatus('profile', 'error', e.message); }
    setSaving(s => ({ ...s, profile: false }));
  };

  const saveEmail = async () => {
    if (!email.trim() || email === user?.email) return;
    setSaving(s => ({ ...s, email: true }));
    try { await updateEmail(email.trim()); setStatus('email', 'success', 'Check your new email for a confirmation link'); }
    catch (e) { setStatus('email', 'error', e.message); }
    setSaving(s => ({ ...s, email: false }));
  };

  const savePassword = async () => {
    if (!newPass) return;
    if (newPass.length < 6) { setStatus('password', 'error', 'At least 6 characters'); return; }
    if (newPass !== confirmPass) { setStatus('password', 'error', 'Passwords do not match'); return; }
    setSaving(s => ({ ...s, password: true }));
    try { await updatePassword(newPass); setNewPass(''); setConfirmPass(''); setStatus('password', 'success', 'Password updated'); }
    catch (e) { setStatus('password', 'error', e.message); }
    setSaving(s => ({ ...s, password: false }));
  };

  const handleManageStripe = async () => {
    try {
      const res = await fetch('/api/billing', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'cancel', customerId: profile.stripe_customer_id }) });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
    } catch (e) { alert(e.message); }
  };

  const handleCancelPlan = async () => {
    setCancelLoading(true);
    try {
      const res = await fetch('/api/billing', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'portal', customerId: profile.stripe_customer_id }) });
      const data = await res.json();
      if (data.canceled) { setCanceledUntil(data.access_until); setShowCancelConfirm(false); await refreshProfile(); }
      else alert(data.error || 'Something went wrong');
    } catch (e) { alert(e.message); }
    setCancelLoading(false);
  };

  const loadTeamMembers = async (teamId) => {
    if (teamMembers[teamId]) return;
    setMemberLoading(m => ({ ...m, [teamId]: true }));
    try { const members = await fetchTeamMembers(teamId); setTeamMembers(m => ({ ...m, [teamId]: members })); }
    catch (e) { console.error(e); }
    setMemberLoading(m => ({ ...m, [teamId]: false }));
  };

  const toggleTeam = (teamId) => {
    const next = expandedTeam === teamId ? null : teamId;
    setExpandedTeam(next);
    if (next) loadTeamMembers(next);
  };

  const handleRemoveMember = async (teamId, userId, username) => {
    if (!window.confirm(`Remove @${username} from this team?`)) return;
    try {
      await removeMember(teamId, userId);
      setTeamMembers(m => ({ ...m, [teamId]: m[teamId].filter(mb => mb.user_id !== userId) }));
    } catch (e) { alert(e.message); }
  };

  const handleRoleChange = async (teamId, userId, newRole) => {
    const key = `${teamId}-${userId}`;
    setRoleWorking(r => ({ ...r, [key]: true }));
    try {
      await updateMemberRole(teamId, userId, newRole);
      setTeamMembers(m => ({ ...m, [teamId]: m[teamId].map(mb => mb.user_id === userId ? { ...mb, role: newRole } : mb) }));
    } catch (e) { alert(e.message); }
    setRoleWorking(r => ({ ...r, [key]: false }));
  };

  const handleGenerateKey = async () => {
    setApiKeyWorking(true); setNewKeyPlaintext('');
    try {
      const result = await generateApiKey();
      analytics.apiKeyGenerated();
      setNewKeyPlaintext(result.key);
      setApiKey({ key_prefix: result.key_prefix, created_at: result.created_at, last_used_at: null });
    } catch (e) { setStatus('apikey', 'error', e.message); }
    setApiKeyWorking(false);
  };

  const handleRevokeKey = async () => {
    setApiKeyWorking(true);
    try { await revokeApiKey(user.id); setApiKey(null); setNewKeyPlaintext(''); setShowRevokeConfirm(false); setStatus('apikey', 'success', 'API key revoked'); }
    catch (e) { setStatus('apikey', 'error', e.message); }
    setApiKeyWorking(false);
  };

  const copyKey = () => {
    navigator.clipboard?.writeText(newKeyPlaintext).catch(() => {});
    setKeyCopied(true); setTimeout(() => setKeyCopied(false), 2000);
  };

  const handleSignOut = async () => { await signOut(); navigate('/app'); };

  const handleRestartTour = async () => {
    if (!user) return;
    await supabase.from('profiles').update({ has_seen_onboarding: false }).eq('id', user.id);
    navigate('/app?tour=1');
  };
  const visibleNav = NAV.filter(n => !n.proOnly || isPro || isAdmin);

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <button onClick={() => navigate('/app')} style={s.backBtn}>←</button>
          <div>
            <div style={s.pageTitle}>Settings</div>
            <div style={s.pageSub}>@{profile?.username}</div>
          </div>
        </div>
      </div>

      <div style={s.layout} className="settings-layout">
        <nav style={s.nav} className="settings-nav">
          {visibleNav.map(n => (
            <button key={n.id} onClick={() => setActiveSection(n.id)} style={{
              ...s.navItem,
              background: activeSection === n.id ? 'rgba(127,119,221,0.1)' : 'transparent',
              color: activeSection === n.id ? '#7f77dd' : 'var(--muted)',
              borderLeft: `2px solid ${activeSection === n.id ? '#7f77dd' : 'transparent'}`,
            }}>
              <span style={{ fontSize: 14, width: 20, textAlign: 'center' }}>{n.icon}</span>
              {n.label}
            </button>
          ))}
        </nav>

        <div style={s.content} className="settings-content">

          {activeSection === 'profile' && (
            <div style={s.sections}>
              <SectionHeader title="Profile" sub="How you appear on CopyPasteGenius" />
              <Card>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 8 }}>
                  <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(127,119,221,0.15)', border: '2px solid rgba(127,119,221,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, fontWeight: 600, color: '#7f77dd', flexShrink: 0 }}>
                    {(profile?.display_name || profile?.username || '?').slice(0, 1).toUpperCase()}
                  </div>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--text)' }}>{profile?.display_name || profile?.username}</div>
                    <div style={{ fontSize: 12, color: 'var(--dim)', marginBottom: 4 }}>@{profile?.username}</div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {(isPro || isAdmin) && <Badge color="#7f77dd">Pro</Badge>}
                      {isAdmin && <Badge color="#ef9f27">Admin</Badge>}
                      {!isPro && !isAdmin && <Badge color="var(--dim)">Free · {entryCount}/{FREE_LIMIT} saves</Badge>}
                    </div>
                  </div>
                </div>
                <Field label="Display name">
                  <input style={s.input} value={displayName} onChange={e => setDisplayName(e.target.value)} placeholder="Your display name" />
                </Field>
                <Row>
                  <StatusMsg success={success.profile} error={errors.profile} />
                  <Btn onClick={saveProfile} loading={saving.profile}>Save</Btn>
                </Row>
              </Card>

              <Card title="Username">
                <div style={{ position: 'relative' }}>
                  <input style={{ ...s.input, paddingRight: 110, color: showUsernameForm ? 'var(--text)' : 'var(--dim)', borderColor: showUsernameForm ? usernameAvail === true ? 'rgba(29,158,117,0.5)' : usernameAvail === false ? 'rgba(226,75,74,0.5)' : undefined : undefined }}
                    value={showUsernameForm ? newUsername : (profile?.username || '')}
                    readOnly={!showUsernameForm}
                    onChange={showUsernameForm ? e => setNewUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '')) : undefined}
                  />
                  {showUsernameForm && newUsername.length >= 3 && (
                    <span style={{ position: 'absolute', right: 80, top: '50%', transform: 'translateY(-50%)', fontSize: 12, color: usernameChecking ? 'var(--dim)' : usernameAvail ? '#5dcaa5' : 'var(--danger)' }}>
                      {usernameChecking ? '…' : usernameAvail === true ? '✓' : '✗'}
                    </span>
                  )}
                  {canChangeUsername && (
                    <button onClick={() => { if (showUsernameForm) { setShowUsernameForm(false); setNewUsername(''); setUsernameAvail(null); } else { setShowUsernameForm(true); setNewUsername(profile?.username || ''); } }}
                      style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', background: 'none', border: '0.5px solid var(--border2)', borderRadius: 4, padding: '3px 10px', fontFamily: 'var(--font)', fontSize: 11, color: 'var(--muted)', cursor: 'pointer' }}>
                      {showUsernameForm ? 'Cancel' : 'Change'}
                    </button>
                  )}
                </div>
                {showUsernameForm && <Hint>Lowercase letters, numbers, underscores · min 3 chars</Hint>}
                {!canChangeUsername && <Hint>Next change available {nextChangeDate} · 7-day cooldown</Hint>}
                {showUsernameForm && (
                  <Row>
                    <StatusMsg success={success.username} error={errors.username} />
                    <Btn onClick={saveUsername} disabled={!usernameAvail} loading={saving.username}>Save username</Btn>
                  </Row>
                )}
              </Card>
            </div>
          )}

          {activeSection === 'security' && (
            <div style={s.sections}>
              <SectionHeader title="Security" sub="Manage your email and password" />
              <Card title="Email address">
                <Field label="Email">
                  <input style={s.input} type="email" value={email} onChange={e => setEmail(e.target.value)} />
                </Field>
                <Row>
                  <StatusMsg success={success.email} error={errors.email} />
                  <Btn onClick={saveEmail} loading={saving.email}>Update email</Btn>
                </Row>
              </Card>
              <Card title="Password">
                <Field label="New password">
                  <input style={s.input} type="password" value={newPass} onChange={e => setNewPass(e.target.value)} placeholder="At least 6 characters" />
                </Field>
                <Field label="Confirm new password">
                  <input style={s.input} type="password" value={confirmPass} onChange={e => setConfirmPass(e.target.value)} placeholder="Repeat new password" />
                </Field>
                <Row>
                  <StatusMsg success={success.password} error={errors.password} />
                  <Btn onClick={savePassword} loading={saving.password}>Update password</Btn>
                </Row>
              </Card>
            </div>
          )}

          {activeSection === 'plan' && (
            <div style={s.sections}>
              <SectionHeader title="Plan & billing" sub="Manage your subscription and payment history" />
              {showBilling && (
                <>
                  <Card>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                          <span style={{ fontSize: 15, fontWeight: 500, color: 'var(--text)' }}>CopyPasteGenius Pro</span>
                          <Badge color="#7f77dd">Active</Badge>
                        </div>
                        {profile?.plan_expires_at && (
                          <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                            {canceledUntil ? `Access until ${new Date(canceledUntil).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}` : `Renews ${new Date(profile.plan_expires_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`}
                          </div>
                        )}
                      </div>
                      <button onClick={handleManageStripe} style={s.outlineBtn}>Manage billing →</button>
                    </div>
                    {canceledUntil && (
                      <div style={{ background: 'rgba(226,75,74,0.08)', border: '0.5px solid rgba(226,75,74,0.25)', borderRadius: 8, padding: '10px 14px', fontSize: 12.5, color: 'var(--muted)', marginTop: 12 }}>
                        Your plan has been canceled. Pro access continues until <strong style={{ color: 'var(--text)' }}>{new Date(canceledUntil).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</strong>.
                      </div>
                    )}
                  </Card>
                  <Card title="Billing history">
                    {invoicesLoading ? <div style={{ fontSize: 12.5, color: 'var(--dim)' }}>Loading...</div>
                    : invoices.length === 0 ? <div style={{ fontSize: 12.5, color: 'var(--dim)' }}>No invoices yet</div>
                    : (
                      <div style={{ border: '0.5px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
                        {invoices.map(inv => (
                          <div key={inv.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 14px', borderBottom: '0.5px solid var(--border)', gap: 12, flexWrap: 'wrap' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                              <span style={{ fontSize: 13, color: 'var(--text)' }}>{inv.description}</span>
                              <span style={{ fontSize: 11, color: 'var(--dim)' }}>{fmt(inv.date)} · #{(inv.number || inv.id.slice(-8)).toUpperCase()}</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                              <span style={{ fontSize: 11, padding: '2px 7px', borderRadius: 4, background: inv.status === 'paid' ? 'rgba(29,158,117,0.12)' : 'rgba(226,75,74,0.12)', color: inv.status === 'paid' ? '#5dcaa5' : 'var(--danger)' }}>{inv.status}</span>
                              <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', minWidth: 52, textAlign: 'right' }}>{fmtAmount(inv.amount, inv.currency)}</span>
                              {inv.pdf && <a href={inv.pdf} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: 'var(--muted)', textDecoration: 'none', border: '0.5px solid var(--border2)', borderRadius: 4, padding: '3px 8px' }}>PDF</a>}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </Card>
                  {!canceledUntil && (
                    <Card title="Cancel plan">
                      {!showCancelConfirm ? (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                          <span style={{ fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.6 }}>You'll keep Pro access until the end of your billing period.</span>
                          <button onClick={() => setShowCancelConfirm(true)} style={{ ...s.outlineBtn, borderColor: 'rgba(226,75,74,0.3)', color: 'var(--danger)' }}>Cancel plan</button>
                        </div>
                      ) : (
                        <div style={{ background: 'rgba(226,75,74,0.07)', border: '0.5px solid rgba(226,75,74,0.25)', borderRadius: 8, padding: '14px' }}>
                          <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', marginBottom: 6 }}>Are you sure?</div>
                          <div style={{ fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.6, marginBottom: 14 }}>
                            Your Pro features stay active until {profile?.plan_expires_at ? new Date(profile.plan_expires_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : 'end of billing period'}.
                          </div>
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button onClick={handleCancelPlan} disabled={cancelLoading} style={{ background: 'var(--danger)', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontFamily: 'var(--font)', fontSize: 12.5, cursor: 'pointer' }}>
                              {cancelLoading ? 'Canceling...' : 'Yes, cancel my plan'}
                            </button>
                            <button onClick={() => setShowCancelConfirm(false)} style={s.outlineBtn}>Keep Pro</button>
                          </div>
                        </div>
                      )}
                    </Card>
                  )}
                </>
              )}
              {!isPro && !isAdmin && (
                <Card>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--text)', marginBottom: 3 }}>Free plan</div>
                      <div style={{ fontSize: 12, color: 'var(--muted)' }}>{entryCount} of {FREE_LIMIT} saves used</div>
                    </div>
                    <Link to="/premium" style={{ padding: '8px 18px', background: '#7f77dd', color: '#fff', borderRadius: 8, fontSize: 13, fontWeight: 500, textDecoration: 'none' }}>Upgrade to Pro →</Link>
                  </div>
                  <div style={{ height: 4, background: 'var(--surface3)', borderRadius: 2, overflow: 'hidden', marginTop: 4 }}>
                    <div style={{ height: '100%', borderRadius: 2, width: `${Math.min((entryCount / FREE_LIMIT) * 100, 100)}%`, background: entryCount >= FREE_LIMIT ? 'var(--danger)' : '#7f77dd', transition: 'width 0.3s' }} />
                  </div>
                </Card>
              )}
            </div>
          )}

          {activeSection === 'teams' && (isPro || isAdmin) && (
            <div style={s.sections}>
              <SectionHeader title="Teams" sub="Manage your teams, members, and seat usage" />

              {/* Plan summary + upgrade CTA */}
              {!teamsLoading && myTeams.length > 0 && myTeams.map(team => {
                const members = teamMembers[team.id] || [];
                const used = members.length;
                const limit = team.seat_limit;
                const pct = Math.min((used / limit) * 100, 100);
                const atLimit = limit !== 999 && used >= limit;
                const nearLimit = limit !== 999 && pct >= 60 && !atLimit;

                const PLAN_INFO = {
                  starter: { label: 'Starter', seats: 3,         color: '#7f77dd' },
                  growth:  { label: 'Growth',  seats: 10,        color: '#1d9e75' },
                  agency:  { label: 'Agency',  seats: 'Unlimited', color: '#ef9f27' },
                };
                const UPGRADES = {
                  starter: { label: 'Growth',  price: '$79/mo', seats: 15,        color: '#1d9e75', perks: ['15 seats', 'Approval workflow', 'Usage analytics'] },
                  growth:  { label: 'Agency',  price: '$149/mo', seats: 'Unlimited', color: '#ef9f27', perks: ['Unlimited seats', 'Priority support', 'Custom onboarding'] },
                };
                const upgrade = UPGRADES[team.plan];
                const plan = PLAN_INFO[team.plan] || PLAN_INFO.starter;

                return (
                  <div key={team.id}>
                    {/* Current plan card */}
                    <div style={{ background: 'var(--surface)', border: '0.5px solid var(--border)', borderRadius: 12, padding: '16px 18px', marginBottom: upgrade ? 12 : 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
                        <div>
                          <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text)', marginBottom: 2 }}>{team.name}</div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Badge color={plan.color}>{plan.label} plan</Badge>
                            <span style={{ fontSize: 12, color: 'var(--muted)' }}>{used} of {limit === 999 ? 'unlimited' : limit} seats used</span>
                          </div>
                        </div>
                        {(atLimit || nearLimit) && (
                          <span style={{ fontSize: 11, padding: '3px 9px', borderRadius: 20, background: atLimit ? 'rgba(226,75,74,0.12)' : 'rgba(239,159,39,0.12)', color: atLimit ? 'var(--danger)' : '#ef9f27', border: `0.5px solid ${atLimit ? 'rgba(226,75,74,0.3)' : 'rgba(239,159,39,0.3)'}`, fontWeight: 500 }}>
                            {atLimit ? '⚠ Seat limit reached' : `${limit - used} seats left`}
                          </span>
                        )}
                      </div>
                      {limit !== 999 && (
                        <div style={{ height: 6, background: 'var(--surface3)', borderRadius: 3, overflow: 'hidden' }}>
                          <div style={{ height: '100%', borderRadius: 3, width: `${pct}%`, background: atLimit ? 'var(--danger)' : nearLimit ? '#ef9f27' : plan.color, transition: 'width 0.3s' }} />
                        </div>
                      )}
                    </div>

                    {/* Upgrade CTA — always shown for non-agency plans */}
                    {upgrade && (
                      <div style={{
                        background: `linear-gradient(135deg, ${upgrade.color}18 0%, ${upgrade.color}08 100%)`,
                        border: `1.5px solid ${upgrade.color}50`,
                        borderRadius: 12, padding: '18px 20px',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        gap: 16, flexWrap: 'wrap',
                      }}>
                        <div style={{ flex: 1, minWidth: 180 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                            <span style={{ fontSize: 18 }}>⚡</span>
                            <span style={{ fontSize: 15, fontWeight: 600, color: upgrade.color }}>
                              Upgrade to {upgrade.label}
                            </span>
                            <span style={{ fontSize: 12, fontWeight: 500, color: upgrade.color, background: `${upgrade.color}20`, padding: '2px 8px', borderRadius: 20 }}>
                              {upgrade.price}
                            </span>
                          </div>
                          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                            {upgrade.perks.map(p => (
                              <span key={p} style={{ fontSize: 12, color: 'var(--muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
                                <span style={{ color: upgrade.color, fontSize: 10 }}>✓</span> {p}
                              </span>
                            ))}
                          </div>
                        </div>
                        <Link to="/premium" style={{
                          display: 'inline-flex', alignItems: 'center', gap: 7,
                          padding: '11px 22px',
                          background: upgrade.color,
                          color: '#fff',
                          borderRadius: 10,
                          fontSize: 14, fontWeight: 600,
                          textDecoration: 'none',
                          whiteSpace: 'nowrap',
                          boxShadow: `0 4px 14px ${upgrade.color}40`,
                          transition: 'transform 0.15s, box-shadow 0.15s',
                        }}
                          onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = `0 6px 20px ${upgrade.color}55`; }}
                          onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = `0 4px 14px ${upgrade.color}40`; }}
                        >
                          Upgrade now →
                        </Link>
                      </div>
                    )}
                  </div>
                );
              })}

              {teamsLoading ? (
                <Card><div style={{ fontSize: 13, color: 'var(--dim)' }}>Loading teams...</div></Card>
              ) : myTeams.length === 0 ? (
                <Card>
                  <div style={{ textAlign: 'center', padding: '20px 0' }}>
                    <div style={{ fontSize: 32, marginBottom: 10 }}>🏢</div>
                    <div style={{ fontSize: 14, color: 'var(--muted)', marginBottom: 16 }}>No teams yet</div>
                    <Link to="/team/create" style={{ padding: '9px 20px', background: '#7f77dd', color: '#fff', borderRadius: 8, fontSize: 13, fontWeight: 500, textDecoration: 'none' }}>+ Create your first team</Link>
                  </div>
                </Card>
              ) : (
                <>
                  {myTeams.map(team => {
                    const isExpanded = expandedTeam === team.id;
                    const members = teamMembers[team.id] || [];
                    const seatUsed = members.length;
                    const seatLimit = team.seat_limit;
                    const seatPct = Math.min((seatUsed / seatLimit) * 100, 100);
                    const isOwner = team.my_role === 'owner';
                    const isTeamAdminRole = team.my_role === 'owner' || team.my_role === 'admin';
                    const avatarColors = ['#7f77dd','#1d9e75','#ef9f27','#e24b4a'];
                    return (
                      <div key={team.id} style={{ border: `1.5px solid ${isExpanded ? 'rgba(127,119,221,0.4)' : 'var(--border)'}`, borderRadius: 12, background: 'var(--surface)', overflow: 'hidden', transition: 'border-color 0.15s' }}>
                        <div onClick={() => toggleTeam(team.id)} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px', cursor: 'pointer' }}>
                          <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(127,119,221,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>🏢</div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text)', marginBottom: 4 }}>{team.name}</div>
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                              <Badge color="#7f77dd">{team.plan}</Badge>
                              <Badge color={seatUsed >= seatLimit ? 'var(--danger)' : 'var(--dim)'}>{isExpanded && memberLoading[team.id] ? '...' : `${seatUsed}/${seatLimit === 999 ? '∞' : seatLimit} seats`}</Badge>
                              <Badge color="var(--dim)">{team.my_role}</Badge>
                            </div>
                          </div>
                          <div style={{ width: 64, flexShrink: 0 }}>
                            <div style={{ height: 5, background: 'var(--surface3)', borderRadius: 3, overflow: 'hidden' }}>
                              <div style={{ height: '100%', borderRadius: 3, width: `${seatPct}%`, background: seatPct >= 100 ? 'var(--danger)' : seatPct >= 80 ? '#ef9f27' : '#7f77dd', transition: 'width 0.3s' }} />
                            </div>
                          </div>
                          <span style={{ fontSize: 12, color: 'var(--dim)', flexShrink: 0 }}>{isExpanded ? '▲' : '▼'}</span>
                        </div>
                        {isExpanded && (
                          <div style={{ borderTop: '0.5px solid var(--border)' }}>
                            {memberLoading[team.id] ? (
                              <div style={{ padding: '12px 18px', fontSize: 13, color: 'var(--dim)' }}>Loading members...</div>
                            ) : members.length === 0 ? (
                              <div style={{ padding: '12px 18px', fontSize: 13, color: 'var(--dim)' }}>No members found</div>
                            ) : members.map(mb => {
                              const isSelf = mb.user_id === user?.id;
                              const roleKey = `${team.id}-${mb.user_id}`;
                              const ac = avatarColors[(mb.username || '').charCodeAt(0) % avatarColors.length];
                              return (
                                <div key={mb.user_id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 18px', borderBottom: '0.5px solid var(--border)' }}>
                                  <div style={{ width: 34, height: 34, borderRadius: '50%', flexShrink: 0, background: `${ac}20`, border: `1.5px solid ${ac}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 600, color: ac }}>
                                    {(mb.display_name || mb.username || '?').slice(0, 2).toUpperCase()}
                                  </div>
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: 13, color: 'var(--text)', fontWeight: 500 }}>
                                      {mb.display_name || mb.username}
                                      {isSelf && <span style={{ fontSize: 10, color: 'var(--dim)', marginLeft: 6, fontWeight: 400 }}>you</span>}
                                    </div>
                                    <div style={{ fontSize: 11, color: 'var(--dim)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{mb.email || `@${mb.username}`}</div>
                                  </div>
                                  {isTeamAdminRole && !isSelf && mb.role !== 'owner' ? (
                                    <select value={mb.role} disabled={roleWorking[roleKey]} onChange={e => handleRoleChange(team.id, mb.user_id, e.target.value)}
                                      style={{ background: 'var(--surface2)', border: '0.5px solid var(--border2)', borderRadius: 6, padding: '4px 8px', fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--font)', cursor: 'pointer' }}>
                                      <option value="member">Member</option>
                                      <option value="admin">Admin</option>
                                    </select>
                                  ) : (
                                    <span style={{ fontSize: 10, padding: '3px 8px', borderRadius: 10, background: mb.role === 'owner' ? 'rgba(239,159,39,0.12)' : mb.role === 'admin' ? 'var(--accent-dim)' : 'var(--surface3)', color: mb.role === 'owner' ? '#ef9f27' : mb.role === 'admin' ? 'var(--accent)' : 'var(--dim)', border: `0.5px solid ${mb.role === 'owner' ? 'rgba(239,159,39,0.3)' : 'transparent'}` }}>{mb.role}</span>
                                  )}
                                  {isOwner && !isSelf && mb.role !== 'owner' && (
                                    <button onClick={() => handleRemoveMember(team.id, mb.user_id, mb.username)}
                                      style={{ background: 'none', border: 'none', color: 'var(--dim)', cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: '0 4px' }}
                                      onMouseEnter={e => e.currentTarget.style.color = 'var(--danger)'}
                                      onMouseLeave={e => e.currentTarget.style.color = 'var(--dim)'}>×</button>
                                  )}
                                </div>
                              );
                            })}
                            <div style={{ padding: '10px 18px', display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                              <Link to="/team" style={{ fontSize: 12, color: '#7f77dd', textDecoration: 'none' }}>→ Dashboard</Link>
                              {isTeamAdminRole && <Link to="/team/settings" style={{ fontSize: 12, color: 'var(--muted)', textDecoration: 'none' }}>⚙ Settings</Link>}
                              <span style={{ fontSize: 11, color: seatUsed >= seatLimit ? 'var(--danger)' : 'var(--dim)', marginLeft: 'auto' }}>
                                {seatLimit === 999 ? 'Unlimited seats' : seatUsed >= seatLimit ? 'Seat limit reached' : `${seatLimit - seatUsed} seat${seatLimit - seatUsed !== 1 ? 's' : ''} available`}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  <Link to="/team/create" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '11px', border: '1px dashed var(--border2)', borderRadius: 12, color: '#7f77dd', fontSize: 13, fontWeight: 500, textDecoration: 'none' }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = '#7f77dd'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border2)'}>
                    + Create another team
                  </Link>
                </>
              )}
            </div>
          )}

          {activeSection === 'developer' && (
            <div style={s.sections}>
              <SectionHeader title="Developer" sub="API access for the Chrome extension and integrations" />
              <Card>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text)', marginBottom: 3 }}>Documentation</div>
                    <div style={{ fontSize: 12, color: 'var(--muted)' }}>API reference, integration guides, and Chrome extension setup</div>
                  </div>
                  <a href="https://copygenius.com/docs" target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', border: '0.5px solid var(--border2)', borderRadius: 8, fontSize: 13, color: 'var(--text)', textDecoration: 'none', background: 'var(--surface2)' }}>
                    📖 CopyGenius Docs →
                  </a>
                </div>
              </Card>
              {/* AI usage quota */}
              {(isPro || isAdmin) && aiUsage && (
                <Card title="AI features usage">
                  <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6, marginBottom: 12 }}>
                    Smart Compress and SOP generation use the Claude API. Your plan includes {aiUsage.limit >= 999 ? 'unlimited' : aiUsage.limit} uses per month.
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--muted)', marginBottom: 5 }}>
                      <span>{aiUsage.total} of {aiUsage.limit >= 999 ? '∞' : aiUsage.limit} used this month</span>
                      <span>Resets {aiUsage.resets}</span>
                    </div>
                    {aiUsage.limit < 999 && (
                      <div style={{ height: 6, background: 'var(--surface3)', borderRadius: 3, overflow: 'hidden' }}>
                        <div style={{
                          height: '100%', borderRadius: 3,
                          width: `${Math.min((aiUsage.total / aiUsage.limit) * 100, 100)}%`,
                          background: aiUsage.total >= aiUsage.limit ? 'var(--danger)' : 'var(--accent)',
                          transition: 'width 0.3s ease',
                        }} />
                      </div>
                    )}
                  </div>
                  {Object.entries(aiUsage.byFeature || {}).length > 0 && (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                      {Object.entries(aiUsage.byFeature).map(([feature, count]) => (
                        <div key={feature} style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: 'var(--surface2)', border: '0.5px solid var(--border)', color: 'var(--muted)' }}>
                          {feature.replace(/_/g, ' ')}: {count}
                        </div>
                      ))}
                    </div>
                  )}
                  {aiUsage.total >= aiUsage.limit && aiUsage.limit < 999 && (
                    <div style={{ marginTop: 10, fontSize: 12, color: 'var(--danger)', background: 'rgba(226,75,74,0.08)', border: '0.5px solid rgba(226,75,74,0.25)', borderRadius: 6, padding: '8px 12px' }}>
                      Monthly limit reached. AI features will resume on {aiUsage.resets}.
                    </div>
                  )}
                </Card>
              )}

              <Card title="API key">
                <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6 }}>
                  Your API key authenticates requests from the Chrome extension. Keep it secret — it grants full access to your account.
                </div>
                {apiKeyLoading ? (
                  <div style={{ fontSize: 13, color: 'var(--dim)' }}>Loading...</div>
                ) : apiKey ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div style={{ background: 'var(--surface2)', border: '0.5px solid var(--border)', borderRadius: 8, padding: '12px 14px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <code style={{ fontFamily: 'var(--mono)', fontSize: 13, color: 'var(--text)', letterSpacing: '0.02em', flex: 1 }}>{apiKey.key_prefix}••••••••••••••••••••••••</code>
                        <Badge color="#5dcaa5">Active</Badge>
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--dim)' }}>
                        Created {new Date(apiKey.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        {apiKey.last_used_at && ` · Last used ${new Date(apiKey.last_used_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}
                      </div>
                    </div>
                    {newKeyPlaintext && (
                      <div style={{ background: 'rgba(29,158,117,0.08)', border: '0.5px solid rgba(29,158,117,0.3)', borderRadius: 8, padding: '12px 14px' }}>
                        <div style={{ fontSize: 11, color: '#5dcaa5', marginBottom: 6, fontWeight: 500 }}>✓ Copy your new key now — it won't be shown again</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <code style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--text)', flex: 1, wordBreak: 'break-all', lineHeight: 1.5 }}>{newKeyPlaintext}</code>
                          <button onClick={copyKey} style={{ flexShrink: 0, background: keyCopied ? 'rgba(29,158,117,0.15)' : 'var(--surface)', border: `0.5px solid ${keyCopied ? 'rgba(29,158,117,0.4)' : 'var(--border2)'}`, borderRadius: 4, padding: '5px 10px', fontFamily: 'var(--font)', fontSize: 11, color: keyCopied ? '#5dcaa5' : 'var(--muted)', cursor: 'pointer' }}>
                            {keyCopied ? '✓ Copied' : 'Copy'}
                          </button>
                        </div>
                      </div>
                    )}
                    <Row>
                      <StatusMsg success={success.apikey} error={errors.apikey} />
                      <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
                        <Btn onClick={handleGenerateKey} loading={apiKeyWorking}>Regenerate</Btn>
                        {!showRevokeConfirm
                          ? <button onClick={() => setShowRevokeConfirm(true)} style={{ ...s.outlineBtn, borderColor: 'rgba(226,75,74,0.4)', color: 'var(--danger)' }}>Revoke</button>
                          : <button onClick={handleRevokeKey} disabled={apiKeyWorking} style={{ ...s.outlineBtn, background: 'rgba(226,75,74,0.1)', borderColor: 'rgba(226,75,74,0.4)', color: 'var(--danger)' }}>Confirm revoke</button>
                        }
                      </div>
                    </Row>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div style={{ background: 'var(--surface2)', border: '0.5px dashed var(--border2)', borderRadius: 8, padding: '20px', textAlign: 'center' }}>
                      <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 12 }}>No API key yet</div>
                      <Btn onClick={handleGenerateKey} loading={apiKeyWorking}>Generate API key</Btn>
                    </div>
                    <StatusMsg success={success.apikey} error={errors.apikey} />
                  </div>
                )}
                <div style={{ fontSize: 12, color: 'var(--dim)', lineHeight: 1.6 }}>
                  Need the Chrome extension?{' '}<span style={{ color: 'var(--accent)' }}>Coming soon</span> — paste your API key into the extension settings to link it to your account.
                </div>
              </Card>
            </div>
          )}

          {activeSection === 'library' && (
            <div style={s.sections}>
              <SectionHeader title="Library" sub="Manage and clean up your saved prompts" />
              <DuplicateFinderCard userId={user?.id} />
            </div>
          )}

          {activeSection === 'session' && (
            <div style={s.sections}>
              <SectionHeader title="Session" sub="Sign out or manage your active session" />
              <Card>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text)', marginBottom: 2 }}>Signed in as</div>
                    <div style={{ fontSize: 12, color: 'var(--muted)' }}>{user?.email}</div>
                  </div>
                  <button onClick={handleSignOut} style={{ background: 'none', border: '0.5px solid rgba(226,75,74,0.35)', borderRadius: 8, padding: '8px 18px', fontFamily: 'var(--font)', fontSize: 13, color: 'var(--danger)', cursor: 'pointer' }}>Sign out</button>
                </div>
              </Card>

              <Card title="App tour">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text)', marginBottom: 2 }}>Walkthrough guide</div>
                    <div style={{ fontSize: 12, color: 'var(--muted)' }}>Re-run the onboarding tour to explore Browse, Folders, Teams, and Skills</div>
                  </div>
                  <button onClick={handleRestartTour} style={{ background: '#7f77dd', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 18px', fontFamily: 'var(--font)', fontSize: 13, fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    ✦ Take the tour
                  </button>
                </div>
              </Card>
            </div>
          )}

        </div>
      </div>

      <style>{`
        @media (max-width: 680px) {
          .settings-layout { flex-direction: column !important; }
          .settings-nav { flex-direction: row !important; overflow-x: auto; border-right: none !important; border-bottom: 0.5px solid var(--border) !important; padding: 6px 0 !important; width: 100% !important; min-width: unset !important; min-height: unset !important; position: static !important; }
          .settings-nav button { border-left: none !important; padding: 8px 14px !important; white-space: nowrap; font-size: 12px !important; }
          .settings-content { padding: 20px 16px !important; max-width: 100% !important; width: 100% !important; }
        }
      `}</style>
    </div>
  );
}

function SectionHeader({ title, sub }) {
  return (
    <div style={{ marginBottom: 4 }}>
      <h2 style={{ fontSize: 20, fontWeight: 500, color: 'var(--text)', marginBottom: 4, letterSpacing: '-0.01em' }}>{title}</h2>
      {sub && <div style={{ fontSize: 13, color: 'var(--muted)' }}>{sub}</div>}
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div style={{ background: 'var(--surface)', border: '0.5px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
      {title && <div style={{ padding: '10px 18px', borderBottom: '0.5px solid var(--border)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--dim)', fontWeight: 500 }}>{title}</div>}
      <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 12 }}>{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <label style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--dim)', fontWeight: 500 }}>{label}</label>
      {children}
    </div>
  );
}

function Hint({ children }) { return <div style={{ fontSize: 11, color: 'var(--dim)', marginTop: 2 }}>{children}</div>; }

function Row({ children }) {
  return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>{children}</div>;
}

function Badge({ color, children }) {
  return <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 20, background: `${color}25`, color, border: `0.5px solid ${color}55`, fontWeight: 500 }}>{children}</span>;
}

function Btn({ onClick, children, loading, disabled }) {
  return (
    <button onClick={onClick} disabled={loading || disabled} style={{ background: '#7f77dd', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 18px', fontFamily: 'var(--font)', fontSize: 13, fontWeight: 500, cursor: (loading || disabled) ? 'not-allowed' : 'pointer', opacity: (loading || disabled) ? 0.6 : 1, whiteSpace: 'nowrap' }}>
      {loading ? 'Saving...' : children}
    </button>
  );
}

function StatusMsg({ success, error }) {
  if (success) return <span style={{ fontSize: 12, color: '#5dcaa5' }}>✓ {success}</span>;
  if (error)   return <span style={{ fontSize: 12, color: 'var(--danger)' }}>{error}</span>;
  return <span />;
}

const s = {
  page:    { minHeight: '100vh', background: 'var(--bg)', fontFamily: 'var(--font)' },
  header:  { padding: '14px 24px', borderBottom: '0.5px solid var(--border)', background: 'var(--surface)' },
  pageTitle: { fontSize: 18, fontWeight: 500, color: 'var(--text)', letterSpacing: '-0.02em' },
  pageSub:   { fontSize: 12, color: 'var(--dim)', marginTop: 1 },
  backBtn: { background: 'none', border: '0.5px solid var(--border2)', borderRadius: 8, width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--muted)', fontFamily: 'var(--font)', fontSize: 16, cursor: 'pointer', flexShrink: 0 },
  layout:  { display: 'flex', alignItems: 'flex-start', minHeight: 'calc(100vh - 57px)' },
  nav:     { width: 200, minWidth: 200, flexShrink: 0, padding: '16px 0', borderRight: '0.5px solid var(--border)', background: 'var(--surface)', minHeight: 'calc(100vh - 57px)', position: 'sticky', top: 0, display: 'flex', flexDirection: 'column', gap: 2 },
  navItem: { display: 'flex', alignItems: 'center', gap: 10, padding: '9px 18px', fontSize: 13, fontWeight: 500, border: 'none', width: '100%', textAlign: 'left', cursor: 'pointer', fontFamily: 'var(--font)', transition: 'all 0.12s', borderLeft: '2px solid transparent', background: 'transparent' },
  content: { flex: 1, padding: '28px 32px', maxWidth: 720, minWidth: 0 },
  sections: { display: 'flex', flexDirection: 'column', gap: 16 },
  input:   { background: 'var(--bg)', border: '0.5px solid var(--border2)', borderRadius: 8, padding: '9px 12px', color: 'var(--text)', fontFamily: 'var(--font)', fontSize: 13, outline: 'none', width: '100%' },
  outlineBtn: { background: 'none', border: '0.5px solid var(--border2)', borderRadius: 8, padding: '7px 14px', fontFamily: 'var(--font)', fontSize: 12.5, color: 'var(--muted)', cursor: 'pointer', whiteSpace: 'nowrap' },
};
